import { Bell, CheckCheck, ShieldCheck, Info } from 'lucide-react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { cn } from '@/utils/cn'
import { apiClient } from '@/services/api.client'
import { useTabsStore } from '@/stores/tabs.store'

const isTauri = typeof window !== 'undefined' && !!(window as { __TAURI__?: unknown }).__TAURI__

type Bildirim = {
  id: string
  tip: string
  baslik: string
  mesaj: string
  link?: string | null
  okundu: boolean
  olusturmaTarihi: string
}

const svc = {
  liste: () =>
    (apiClient.get('/bildirimler?limit=20') as Promise<{ data: { data: Bildirim[]; okunmamis: number } }>)
      .then((r) => r.data),
  oku: (id: string) => apiClient.put(`/bildirimler/${id}/oku`, {}),
  tumunuOku: () => apiClient.put('/bildirimler/tumunu-oku', {}),
}

function zaman(t: string): string {
  const d = new Date(t)
  const fark = Date.now() - d.getTime()
  const dk = Math.floor(fark / 60000)
  if (dk < 1) return 'az önce'
  if (dk < 60) return `${dk} dk önce`
  const sa = Math.floor(dk / 60)
  if (sa < 24) return `${sa} saat önce`
  return d.toLocaleDateString('tr-TR')
}

export function BildirimMenu() {
  const navigate = useNavigate()
  const qc = useQueryClient()

  const { data } = useQuery({
    queryKey: ['bildirimler'],
    queryFn: svc.liste,
    refetchInterval: 60_000,
    staleTime: 30_000,
  })

  const okuMut = useMutation({
    mutationFn: svc.oku,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bildirimler'] }),
  })
  const tumunuMut = useMutation({
    mutationFn: svc.tumunuOku,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bildirimler'] }),
  })

  const bildirimler = data?.data ?? []
  const okunmamis = data?.okunmamis ?? 0

  const tikla = (b: Bildirim) => {
    if (!b.okundu) okuMut.mutate(b.id)
    if (b.link) {
      if (isTauri) useTabsStore.getState().openTab(b.link, b.baslik)
      else navigate(b.link)
    }
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          className="relative w-8 h-8 flex items-center justify-center rounded-md text-text-muted hover:text-text-primary hover:bg-bg-elevated transition-colors"
          title="Bildirimler"
        >
          <Bell className="w-4 h-4" />
          {okunmamis > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 flex items-center justify-center text-[10px] font-bold text-white bg-error rounded-full">
              {okunmamis > 9 ? '9+' : okunmamis}
            </span>
          )}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          className="w-80 max-w-[92vw] bg-bg-surface border border-bg-border rounded-lg shadow-dropdown p-1 z-50 animate-slide-in-up"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-bg-border mb-1">
            <p className="text-sm font-medium text-text-primary">Bildirimler</p>
            {okunmamis > 0 && (
              <button
                onClick={() => tumunuMut.mutate()}
                className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
              >
                <CheckCheck className="w-3.5 h-3.5" /> Tümünü Oku
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {bildirimler.length === 0 ? (
              <div className="px-3 py-8 text-center text-sm text-text-muted">Bildirim yok</div>
            ) : (
              bildirimler.map((b) => (
                <button
                  key={b.id}
                  onClick={() => tikla(b)}
                  className={cn(
                    'w-full text-left flex gap-2.5 px-3 py-2.5 rounded-md transition-colors',
                    b.okundu ? 'hover:bg-bg-elevated' : 'bg-primary/5 hover:bg-primary/10'
                  )}
                >
                  <span className={cn('mt-0.5 shrink-0', b.tip === 'onay' ? 'text-warning' : 'text-info')}>
                    {b.tip === 'onay' ? <ShieldCheck className="w-4 h-4" /> : <Info className="w-4 h-4" />}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-1.5">
                      <span className="text-sm font-medium text-text-primary truncate">{b.baslik}</span>
                      {!b.okundu && <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />}
                    </span>
                    <span className="block text-xs text-text-secondary mt-0.5 line-clamp-2">{b.mesaj}</span>
                    <span className="block text-[11px] text-text-muted mt-0.5">{zaman(b.olusturmaTarihi)}</span>
                  </span>
                </button>
              ))
            )}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
